// src/pages/ForgotPassword.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import Card from "../../components/ui/Card.jsx";
import { Mail, Send, ArrowLeft, ShieldCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = React.useState("");
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState("");
  const navigate = useNavigate();

  const onSubmit = (e) => {
    e.preventDefault();
    if (!email.trim()) return setError("Please enter your email.");
    setError("");
    // TODO: call your API to initiate reset email
    setSent(true);
  };

  return (
    <>
      <style>{`
        @keyframes slowFloat { 0% { transform: translateY(0) } 50% { transform: translateY(-6px) } 100% { transform: translateY(0) } }
        .logo-float { animation: slowFloat 6s ease-in-out infinite; }
        @keyframes glow { 0% { box-shadow: 0 0 0 rgba(56,189,248,0.0) } 50% { box-shadow: 0 8px 24px rgba(56,189,248,0.25) } 100% { box-shadow: 0 0 0 rgba(56,189,248,0.0) } }
        .card-glow { animation: glow 6s ease-in-out infinite; }
      `}</style>

      <div className="min-h-screen grid md:grid-cols-2 bg-slate-50">
        {/* Brand / Left panel (same look for consistency) */}
        <div className="hidden md:flex relative items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-sky-500 to-sky-600" />
          <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-20 -right-10 h-72 w-72 rounded-full bg-white/10 blur-2xl" />
          <div className="relative z-10 max-w-md px-10 text-white">
            <div className="flex items-center gap-4">
              <div className="md:h-28 md:w-28 h-20 w-20 rounded-2xl bg-white/10 grid place-items-center ring-1 ring-white/30 overflow-hidden">
                <img
                  src="/dynesourcer-logo.svg"
                  alt="Dynesourcer"
                  className="h-24 w-auto md:h-36 logo-float scale-[1.7] md:scale-[1.9] origin-center select-none pointer-events-none"
                  draggable="false"
                  loading="eager"
                  width="144"
                  height="144"
                />
              </div>
              <div>
                <h1 className="md:text-3xl text-2xl font-extrabold tracking-tight">Dynesourcer</h1>
                <p className="text-white/80 md:text-sm text-xs -mt-0.5">Lender Application Portal</p>
              </div>
            </div>

            <div className="mt-8 space-y-3">
              <h2 className="text-3xl font-bold leading-tight">Reset your password</h2>
              <p className="text-white/85">
                Enter the email associated with your account. If it exists, we’ll send password reset
                instructions.
              </p>
            </div>
          </div>
        </div>

        {/* Reset Card / Right panel */}
        <div className="flex items-center justify-center p-6">
          <Card className="w-full max-w-md rounded-2xl border-slate-200 bg-white p-6 shadow-sm card-glow">
            <div className="mb-6">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-sky-500" />
                <h2 className="text-xl font-semibold text-slate-900">Forgot password</h2>
              </div>
              <p className="mt-1 text-sm text-slate-600">
                We’ll email you a link to create a new password.
              </p>
            </div>

            {sent ? (
              <div className="space-y-4">
                <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-700">
                  If an account exists for <span className="font-semibold">{email}</span>, a reset link has been sent.
                  Please check your inbox (and spam).
                </div>
                <button
                  onClick={() => navigate("/signin")}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2 text-slate-700 hover:bg-slate-50"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to sign in
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4">
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-slate-700">Email</span>
                  <div className="relative">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 pr-10 outline-none focus:ring-2 focus:ring-sky-200"
                      placeholder="you@dynesourcer.com"
                      autoComplete="email"
                      aria-invalid={!!error && !email.trim()}
                    />
                    <Mail className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  </div>
                </label>

                {error && <p className="text-sm text-rose-600">{error}</p>}

                <div className="flex items-center gap-3">
                  <button
                    type="submit"
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-sky-500 px-4 py-2.5 font-semibold text-white shadow hover:bg-sky-600"
                  >
                    <Send className="h-4 w-4" />
                    Send reset link
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate("/signin")}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2 text-slate-700 hover:bg-slate-50"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </button>
                </div>
              </form>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}

